import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Loader2, Save, Send, Sparkles, Mail, Clock, ArrowLeft, AlertTriangle, Eye,
  Plus, Trash2, ChevronUp, ChevronDown, Users, Layers, Pause, Play,
} from 'lucide-react';
import { toast } from 'sonner';

const MERGE_TAGS = ['first_name', 'last_name', 'email', 'unsubscribe_url'];

const renderPreview = (text) => (text || '')
  .replace(/\{\{\s*first_name\s*\}\}/g, 'Jane')
  .replace(/\{\{\s*last_name\s*\}\}/g, 'Doe')
  .replace(/\{\{\s*email\s*\}\}/g, 'jane@example.com')
  .replace(/\{\{\s*unsubscribe_url\s*\}\}/g, 'https://…/unsubscribe');

const TagButtons = ({ onInsert, idPrefix }) => (
  <div className="flex flex-wrap items-center gap-1.5 pt-1">
    <span className="text-xs text-zinc-400">Insert:</span>
    {MERGE_TAGS.map((t) => (
      <button key={t} type="button" onClick={() => onInsert(t)} className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100" data-testid={`${idPrefix}-insert-${t}`}>{`{{${t}}}`}</button>
    ))}
  </div>
);

export const EmailCampaignEditorPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isNew = !id;
  const initialType = isNew ? (sp.get('type') === 'sequence' ? 'sequence' : 'broadcast') : 'broadcast';

  const [loading, setLoading] = useState(!isNew);
  const [lists, setLists] = useState([]);
  const [type, setType] = useState(initialType);
  const [campaign, setCampaign] = useState({ name: '', subject: '', body_text: '', list_ids: [], tag_filter: [] });
  const [steps, setSteps] = useState([{ delay_days: 0, delay_hours: 0, subject: '', body_text: '' }]);
  const [tagFilterText, setTagFilterText] = useState('');
  const [savedId, setSavedId] = useState(id || null);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState(null);
  const [status, setStatus] = useState('draft');
  const [enrollCounts, setEnrollCounts] = useState(null);

  const [showAI, setShowAI] = useState(false);
  const [aiTarget, setAiTarget] = useState(null); // null = broadcast body, number = step idx
  const [showTest, setShowTest] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const bodyRef = useRef(null);

  const loadEnrollments = async (cid) => {
    try { const r = await api.get(`/email/sequences/${cid}/enrollments`); setEnrollCounts(r.data?.counts); } catch { /* ignore */ }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [l, p] = await Promise.all([api.get('/email/lists'), api.get('/email/providers')]);
        if (!mounted) return;
        setLists(l.data?.lists || []);
        setProvider((p.data?.providers || [])[0] || null);
        if (!isNew) {
          const c = await api.get(`/email/campaigns/${id}`);
          if (!mounted) return;
          setType(c.data.type || 'broadcast');
          setStatus(c.data.status || 'draft');
          setCampaign({ name: c.data.name, subject: c.data.subject || '', body_text: c.data.body_text || '', list_ids: c.data.list_ids || [], tag_filter: c.data.tag_filter || [] });
          setTagFilterText((c.data.tag_filter || []).join(', '));
          if (c.data.type === 'sequence') {
            setSteps((c.data.steps || []).length ? c.data.steps : [{ delay_days: 0, delay_hours: 0, subject: '', body_text: '' }]);
            loadEnrollments(id);
          }
        }
      } catch { toast.error('Could not load campaign'); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [api, id, isNew]);

  const insertBroadcastTag = (tag) => {
    const el = bodyRef.current;
    const token = `{{${tag}}}`;
    if (!el) { setCampaign((c) => ({ ...c, body_text: (c.body_text || '') + token })); return; }
    const start = el.selectionStart ?? campaign.body_text.length;
    const end = el.selectionEnd ?? campaign.body_text.length;
    const next = campaign.body_text.slice(0, start) + token + campaign.body_text.slice(end);
    setCampaign((c) => ({ ...c, body_text: next }));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const updateStep = (i, patch) => setSteps((s) => s.map((st, idx) => idx === i ? { ...st, ...patch } : st));
  const insertStepTag = (i, tag) => updateStep(i, { body_text: (steps[i].body_text || '') + `{{${tag}}}` });
  const addStep = () => setSteps((s) => [...s, { delay_days: s.length ? 3 : 0, delay_hours: 0, subject: '', body_text: '' }]);
  const removeStep = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => setSteps((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const copy = [...s]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });

  const buildPayload = () => ({
    name: campaign.name.trim() || 'Untitled campaign',
    type,
    subject: campaign.subject,
    body_text: campaign.body_text,
    list_ids: campaign.list_ids,
    tag_filter: tagFilterText.split(',').map((t) => t.trim()).filter(Boolean),
    ...(type === 'sequence' ? { steps } : {}),
  });

  const persist = async () => {
    const payload = buildPayload();
    if (savedId) { await api.put(`/email/campaigns/${savedId}`, payload); return savedId; }
    const r = await api.post('/email/campaigns', payload);
    setSavedId(r.data.id);
    return r.data.id;
  };

  const saveDraft = async () => {
    if (!campaign.name.trim()) return toast.error('Give your campaign a name');
    setSaving(true);
    try {
      const cid = await persist();
      toast.success('Saved');
      if (isNew) navigate(`/email/campaigns/${cid}/edit`, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const senderReady = provider && provider.sender_verified;
  const toggleList = (lid) => setCampaign((c) => ({ ...c, list_ids: c.list_ids.includes(lid) ? c.list_ids.filter((x) => x !== lid) : [...c.list_ids, lid] }));

  const applyAI = (d) => {
    if (aiTarget === null) setCampaign((c) => ({ ...c, subject: d.subject || c.subject, body_text: d.body_text || c.body_text }));
    else updateStep(aiTarget, { subject: d.subject || steps[aiTarget].subject, body_text: d.body_text || steps[aiTarget].body_text });
  };

  if (loading) {
    return <EmailSectionLayout><div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div></EmailSectionLayout>;
  }

  const isSeq = type === 'sequence';

  return (
    <EmailSectionLayout
      actions={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => navigate('/email/campaigns')}><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
          <Button variant="outline" onClick={saveDraft} disabled={saving} data-testid="save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-2 text-sm text-zinc-500">
        {isSeq ? <Layers className="w-4 h-4 text-violet-600" /> : <Mail className="w-4 h-4 text-indigo-600" />}
        <span className="font-medium text-zinc-700">{isSeq ? 'Drip sequence' : 'Broadcast'}</span>
        <Badge variant="secondary" className="capitalize">{status}</Badge>
      </div>

      {!senderReady && (
        <Card className="border-amber-200 bg-amber-50" data-testid="sender-warning">
          <CardContent className="p-4 flex items-center gap-3 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            {!provider ? 'Connect SendGrid to send this campaign.' : 'Your sender email isn\u2019t verified yet — you can draft, but sending is disabled until verified.'}
            <Button variant="link" className="text-amber-700 h-auto p-0" onClick={() => navigate('/email/providers')}>Fix it →</Button>
          </CardContent>
        </Card>
      )}

      <div className="space-y-1.5 max-w-md">
        <Label>Campaign name <span className="text-zinc-400 font-normal">(internal)</span></Label>
        <Input value={campaign.name} onChange={(e) => setCampaign({ ...campaign, name: e.target.value })} placeholder={isSeq ? 'Welcome drip' : 'June newsletter'} data-testid="campaign-name-input" />
      </div>

      {isSeq ? (
        <SequenceEditor
          steps={steps} updateStep={updateStep} insertStepTag={insertStepTag} addStep={addStep} removeStep={removeStep} moveStep={moveStep}
          openAI={(i) => { setAiTarget(i); setShowAI(true); }}
          lists={lists} savedId={savedId} senderReady={senderReady} persist={persist}
          enrollCounts={enrollCounts} reloadEnroll={() => loadEnrollments(savedId)}
          status={status} setStatus={setStatus} api={api}
        />
      ) : (
        <BroadcastEditor
          campaign={campaign} setCampaign={setCampaign} bodyRef={bodyRef} insertBroadcastTag={insertBroadcastTag}
          openAI={() => { setAiTarget(null); setShowAI(true); }} lists={lists} toggleList={toggleList}
          tagFilterText={tagFilterText} setTagFilterText={setTagFilterText} provider={provider} senderReady={senderReady}
          savedId={savedId} persist={persist} onTest={() => setShowTest(true)} onSchedule={(m) => setShowSchedule(m)}
        />
      )}

      <AIDialog open={showAI} onClose={() => setShowAI(false)} onApply={applyAI} />
      <TestSendDialog open={showTest} onClose={() => setShowTest(false)} campaignId={savedId} />
      <SendDialog mode={showSchedule} onClose={() => setShowSchedule(false)} persist={persist} onDone={() => navigate('/email/campaigns')} />
    </EmailSectionLayout>
  );
};

// ----- Broadcast -----
const BroadcastEditor = ({ campaign, setCampaign, bodyRef, insertBroadcastTag, openAI, lists, toggleList, tagFilterText, setTagFilterText, provider, senderReady, savedId, persist, onTest, onSchedule }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-indigo-600" /> Compose</span>
          <Button variant="outline" size="sm" onClick={openAI} data-testid="ai-generate-btn"><Sparkles className="w-3.5 h-3.5 mr-1 text-indigo-600" /> Generate with AI</Button>
        </CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5"><Label>Subject</Label><Input value={campaign.subject} onChange={(e) => setCampaign({ ...campaign, subject: e.target.value })} placeholder="Quick update" data-testid="campaign-subject-input" /></div>
          <div className="space-y-1.5">
            <Label>Body (plain text)</Label>
            <Textarea ref={bodyRef} value={campaign.body_text} onChange={(e) => setCampaign({ ...campaign, body_text: e.target.value })} rows={12} className="font-mono text-sm" placeholder={'Hi {{first_name}},\n\nQuick note —'} data-testid="campaign-body-input" />
            <TagButtons onInsert={insertBroadcastTag} idPrefix="body" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm">Audience</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs text-zinc-500">Send to lists</Label>
            {lists.length === 0 ? <p className="text-sm text-zinc-400">No lists yet. Leave empty to send to all active subscribers.</p> :
              lists.map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={campaign.list_ids.includes(l.id)} onCheckedChange={() => toggleList(l.id)} data-testid={`audience-list-${l.id}`} />
                  {l.name} <span className="text-zinc-400">({l.subscriber_count})</span>
                </label>
              ))}
            <p className="text-xs text-zinc-400">Leave all unchecked to send to every active subscriber.</p>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-zinc-500">Filter by tags (optional)</Label><Input value={tagFilterText} onChange={(e) => setTagFilterText(e.target.value)} placeholder="vip, lead-magnet" data-testid="campaign-tagfilter-input" /></div>
        </CardContent>
      </Card>
    </div>

    <div className="space-y-4">
      <Card className="lg:sticky lg:top-4">
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-1.5"><Eye className="w-4 h-4 text-indigo-600" /> Preview</CardTitle></CardHeader>
        <CardContent>
          <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="campaign-preview">
            <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200 space-y-0.5">
              <div className="text-xs text-zinc-400">From: {provider?.sender_name || 'Your sender'} &lt;{provider?.sender_email || 'verify a sender'}&gt;</div>
              <div className="text-sm font-semibold text-zinc-900">{renderPreview(campaign.subject) || <span className="italic text-zinc-400">No subject</span>}</div>
            </div>
            <div className="px-4 py-4 whitespace-pre-wrap text-sm text-zinc-700 min-h-[180px]">
              {renderPreview(campaign.body_text) || <span className="italic text-zinc-400">Your email body…</span>}
              <div className="mt-6 pt-3 border-t border-zinc-100 text-xs text-zinc-400">— Unsubscribe link appended automatically.</div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 space-y-2">
          <Button variant="outline" className="w-full" onClick={async () => { if (!savedId) { try { await persist(); } catch { return toast.error('Save first'); } } onTest(); }} disabled={!senderReady} data-testid="test-send-btn"><Send className="w-4 h-4 mr-1.5" /> Send a test to yourself</Button>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => onSchedule(true)} disabled={!senderReady} data-testid="open-schedule-btn"><Clock className="w-4 h-4 mr-1.5" /> Schedule</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => onSchedule('now')} disabled={!senderReady} data-testid="open-sendnow-btn"><Send className="w-4 h-4 mr-1.5" /> Send now</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

// ----- Sequence -----
const SequenceEditor = ({ steps, updateStep, insertStepTag, addStep, removeStep, moveStep, openAI, lists, savedId, senderReady, persist, enrollCounts, reloadEnroll, status, setStatus, api }) => {
  const [enrollList, setEnrollList] = useState('all');
  const [enrolling, setEnrolling] = useState(false);
  const [busy, setBusy] = useState(false);

  const enroll = async () => {
    if (!savedId) { try { await persist(); } catch { return toast.error('Save the sequence first'); } }
    setEnrolling(true);
    try {
      const body = enrollList === 'all' ? { list_ids: [] } : { list_ids: [enrollList] };
      const r = await api.post(`/email/sequences/${savedId}/enroll`, body);
      toast.success(`Enrolled ${r.data.enrolled} subscriber${r.data.enrolled === 1 ? '' : 's'}`);
      setStatus('active');
      reloadEnroll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Enroll failed'); }
    finally { setEnrolling(false); }
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      if (status === 'paused') { await api.post(`/email/campaigns/${savedId}/resume`); setStatus('active'); toast.success('Sequence resumed'); }
      else { await api.post(`/email/campaigns/${savedId}/pause`); setStatus('paused'); toast.success('Sequence paused'); }
    } catch { toast.error('Action failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4" data-testid="sequence-steps">
        {steps.map((st, i) => (
          <Card key={i} data-testid={`step-card-${i}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs flex items-center justify-center font-semibold">{i + 1}</span> Step {i + 1}</span>
                <span className="flex items-center gap-1">
                  <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30" data-testid={`step-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30" data-testid={`step-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
                  <Button variant="ghost" size="sm" onClick={() => openAI(i)} data-testid={`step-ai-${i}`}><Sparkles className="w-3.5 h-3.5 text-indigo-600" /></Button>
                  {steps.length > 1 && <button onClick={() => removeStep(i)} className="p-1 text-zinc-400 hover:text-red-600" data-testid={`step-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-zinc-400" />
                <span className="text-zinc-500">{i === 0 ? 'Send' : 'Wait'}</span>
                <Input type="number" min="0" value={st.delay_days} onChange={(e) => updateStep(i, { delay_days: e.target.value })} className="w-16 h-8" data-testid={`step-days-${i}`} />
                <span className="text-zinc-500">days</span>
                <Input type="number" min="0" max="23" value={st.delay_hours} onChange={(e) => updateStep(i, { delay_hours: e.target.value })} className="w-16 h-8" data-testid={`step-hours-${i}`} />
                <span className="text-zinc-500">hours {i === 0 ? 'after enrollment' : 'after previous'}</span>
              </div>
              <div className="space-y-1.5"><Label className="text-xs text-zinc-500">Subject</Label><Input value={st.subject} onChange={(e) => updateStep(i, { subject: e.target.value })} placeholder="Subject line" data-testid={`step-subject-${i}`} /></div>
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-500">Body</Label>
                <Textarea value={st.body_text} onChange={(e) => updateStep(i, { body_text: e.target.value })} rows={6} className="font-mono text-sm" placeholder={'Hi {{first_name}},'} data-testid={`step-body-${i}`} />
                <TagButtons onInsert={(t) => insertStepTag(i, t)} idPrefix={`step-${i}`} />
              </div>
            </CardContent>
          </Card>
        ))}
        <Button variant="outline" onClick={addStep} className="w-full" data-testid="add-step-btn"><Plus className="w-4 h-4 mr-1.5" /> Add step</Button>
      </div>

      <div className="space-y-4">
        <Card className="lg:sticky lg:top-4">
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-1.5"><Users className="w-4 h-4 text-violet-600" /> Enroll subscribers</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {enrollCounts && (
              <div className="flex gap-2 text-xs">
                <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100" data-testid="enroll-active">{enrollCounts.active} active</Badge>
                <Badge variant="secondary" data-testid="enroll-completed">{enrollCounts.completed} done</Badge>
              </div>
            )}
            <Select value={enrollList} onValueChange={setEnrollList}>
              <SelectTrigger data-testid="enroll-list-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All active subscribers</SelectItem>
                {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.subscriber_count})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={enroll} disabled={enrolling || !senderReady} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="enroll-btn">
              {enrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />} Enroll
            </Button>
            {!senderReady && <p className="text-xs text-amber-600">Connect &amp; verify SendGrid to enroll.</p>}
            {savedId && status !== 'draft' && (
              <Button variant="outline" onClick={togglePause} disabled={busy} className="w-full" data-testid="pause-resume-btn">
                {status === 'paused' ? <><Play className="w-4 h-4 mr-1.5" /> Resume sequence</> : <><Pause className="w-4 h-4 mr-1.5" /> Pause sequence</>}
              </Button>
            )}
            <p className="text-xs text-zinc-400">Subscribers move through steps automatically based on each step&apos;s delay.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AIDialog = ({ open, onClose, onApply }) => {
  const { api } = useAuth();
  const [form, setForm] = useState({ purpose: '', audience: '', tone: 'friendly' });
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!form.purpose.trim()) return toast.error('Describe the email purpose');
    setBusy(true);
    try { const r = await api.post('/email/ai-draft', form); onApply(r.data); toast.success('Draft generated — review & edit'); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="ai-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-600" /> Generate email with AI</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>What&apos;s this email about?</Label><Textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Welcome new subscribers and point them to our getting-started guide" data-testid="ai-purpose-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Audience</Label><Input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder="New trial users" data-testid="ai-audience-input" /></div>
            <div className="space-y-1.5"><Label>Tone</Label>
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{['friendly', 'professional', 'casual', 'urgent', 'inspirational'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={gen} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-generate-confirm">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Generate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TestSendDialog = ({ open, onClose, campaignId }) => {
  const { api, user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!campaignId) return toast.error('Save the draft first');
    setBusy(true);
    try { await api.post(`/email/campaigns/${campaignId}/test-send`, { recipient_email: email }); toast.success(`Test sent to ${email}`); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="test-dialog">
        <DialogHeader><DialogTitle>Send a test email</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Recipient</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="test-email-input" /></div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-test-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Send test</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SendDialog = ({ mode, onClose, persist, onDone }) => {
  const { api } = useAuth();
  const isNow = mode === 'now';
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const cid = await persist();
      if (isNow) { const r = await api.post(`/email/campaigns/${cid}/send-now`); toast.success(`Queued ${r.data.queued} email${r.data.queued === 1 ? '' : 's'}`); }
      else { if (!when) { setBusy(false); return toast.error('Pick a date & time'); } const iso = new Date(when).toISOString(); const r = await api.post(`/email/campaigns/${cid}/schedule`, { scheduled_for: iso }); toast.success(`Scheduled ${r.data.queued} email${r.data.queued === 1 ? '' : 's'}`); }
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!mode} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="send-dialog">
        <DialogHeader><DialogTitle>{isNow ? 'Send now' : 'Schedule send'}</DialogTitle></DialogHeader>
        {isNow ? <p className="text-sm text-zinc-600">This queues the email to every active subscriber matching your audience. Continue?</p>
          : <div className="space-y-1.5"><Label>Send at</Label><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="schedule-datetime" /></div>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={go} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-send-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{isNow ? 'Send now' : 'Schedule'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailCampaignEditorPage;
